import React from 'react';
import { useNavigate } from 'react-router-dom';
import { cn } from '../utils/cn';
import { TrendingUp, TrendingDown } from 'lucide-react';

/**
 * KPI Card Component - Molecule
 * Displays key performance indicators
 * 
 * @param {Object} props
 * @param {string} props.title - KPI title
 * @param {string|number} props.value - KPI value
 * @param {string} props.change - Change percentage or text
 * @param {boolean} props.isPositive - Whether change is positive
 * @param {React.ReactNode} props.icon - Icon component
 * @param {string} props.color - Card color variant
 * @param {string} props.href - Optional href to navigate to when clicked
 * @param {Function} props.onClick - Optional click handler
 */
const KPICard = ({
  title,
  value,
  change,
  isPositive,
  icon,
  color = 'primary',
  className = '',
  href,
  onClick,
  ...props
}) => {
  const navigate = useNavigate();

  const handleClick = () => {
    if (href) {
      navigate(href);
    } else if (onClick) {
      onClick();
    }
  };

  const isClickable = href || onClick;
  const colorVariants = {
    // Primary - Lighter Emerald Green (main KPI color) - Light to medium gradient
    primary: 'bg-gradient-to-br from-primary-400 to-primary-500 dark:from-primary-600 dark:to-primary-700 text-white border-2 border-primary-300/50 dark:border-primary-500 shadow-lg shadow-primary-400/25 dark:shadow-primary-600/30',
    // Secondary - Lighter Deep Teal (alternative KPI color) - Light to medium gradient
    secondary: 'bg-gradient-to-br from-secondary-400 to-secondary-500 dark:from-secondary-600 dark:to-secondary-700 text-white border-2 border-secondary-300/50 dark:border-secondary-500 shadow-lg shadow-secondary-400/25 dark:shadow-secondary-600/30',
    // Success - Very light emerald for positive metrics - Bright light gradient
    success: 'bg-gradient-to-br from-primary-300 to-primary-400 dark:from-primary-500 dark:to-primary-600 text-white border-2 border-primary-200/50 dark:border-primary-400 shadow-lg shadow-primary-300/30 dark:shadow-primary-500/30',
    // Info - Blue for informational metrics
    info: 'bg-gradient-to-br from-info-500 to-info-600 dark:from-info-600 dark:to-info-700 text-white border-2 border-info-400/50 dark:border-info-500 shadow-lg shadow-info-500/25 dark:shadow-info-600/30',
    // Accent - Silver for neutral metrics - Subtle gradient
    accent: 'bg-gradient-to-br from-accent-500 to-accent-600 dark:from-accent-600 dark:to-accent-700 text-white border-2 border-accent-400/50 dark:border-accent-500 shadow-lg shadow-accent-500/20 dark:shadow-accent-600/25',
    // Error - Red for negative metrics
    error: 'bg-gradient-to-br from-error-500 to-error-600 dark:from-error-600 dark:to-error-700 text-white border-2 border-error-400/50 dark:border-error-500 shadow-lg shadow-error-500/20 dark:shadow-error-700/30',
    // Danger - Alias for Error (Red)
    danger: 'bg-gradient-to-br from-error-500 to-error-600 dark:from-error-600 dark:to-error-700 text-white border-2 border-error-400/50 dark:border-error-500 shadow-lg shadow-error-500/20 dark:shadow-error-700/30',
    // Warning - Orange/Amber for warnings
    warning: 'bg-gradient-to-br from-warning-500 to-warning-600 dark:from-warning-600 dark:to-warning-700 text-white border-2 border-warning-400/50 dark:border-warning-500 shadow-lg shadow-warning-500/20 dark:shadow-warning-700/30',
    // Neutral - Light background with emerald text - Subtle gradient
    neutral: 'bg-gradient-to-br from-primary-50 to-primary-100 dark:from-primary-800 dark:to-primary-900 border-2 border-primary-200 dark:border-primary-700 text-primary-900 dark:text-primary-50 shadow-md shadow-primary-200/20 dark:shadow-primary-800/20',
  };

  const isNeutral = color === 'neutral';

  return (
    <div
      className={cn(
        'rounded-xl p-6 transition-all duration-200 ease-out',
        isClickable ? 'cursor-pointer' : 'cursor-default',
        /* Light drop shadow for KPI cards - soft, diffused, elegant */
        'shadow-[0_2px_8px_0_rgba(0,0,0,0.08),0_1px_4px_-1px_rgba(0,0,0,0.06)]',
        'dark:shadow-[0_2px_8px_0_rgba(0,0,0,0.4),0_1px_4px_-1px_rgba(0,0,0,0.3)]',
        /* Hover: enhanced shadow with micro lift */
        isClickable && 'hover:shadow-[0_6px_12px_0_rgba(0,0,0,0.1),0_2px_6px_-1px_rgba(0,0,0,0.08)]',
        isClickable && 'dark:hover:shadow-[0_6px_12px_0_rgba(0,0,0,0.5),0_2px_6px_-1px_rgba(0,0,0,0.4)]',
        isClickable && 'hover:-translate-y-1 active:translate-y-0',
        colorVariants[color],
        className
      )}
      onClick={isClickable ? handleClick : undefined}
      role={isClickable ? 'button' : undefined}
      tabIndex={isClickable ? 0 : undefined}
      onKeyDown={isClickable ? (e) => {
        if (e.key === 'Enter' || e.key === ' ') {
          e.preventDefault();
          handleClick();
        }
      } : undefined}
      {...props}
    >
      <div className="flex items-center justify-between mb-4">
        <p className={cn(
          'text-sm font-medium',
          isNeutral ? 'text-primary-600 dark:text-primary-400' : 'text-white/90'
        )}>{title}</p>
        {icon && (
          <div className={cn(
            isNeutral ? 'text-primary-500 dark:text-primary-400' : 'opacity-90 text-white'
          )}>{icon}</div>
        )}
      </div>
      <div className="flex items-baseline justify-between">
        <p className={cn(
          'text-3xl font-bold',
          isNeutral ? 'text-primary-900 dark:text-primary-50' : 'text-white'
        )}>{value}</p>
        {change && (
          <div
            className={cn(
              'flex items-center gap-1 text-sm font-medium',
              isNeutral
                ? 'text-primary-600 dark:text-primary-400'
                : isPositive !== undefined
                  ? (isPositive ? 'text-white/90' : 'text-white/80')
                  : 'text-white/80'
            )}
          >
            {isPositive !== undefined && (
              isPositive ? (
                <TrendingUp size={16} />
              ) : (
                <TrendingDown size={16} />
              )
            )}
            <span>{change}</span>
          </div>
        )}
      </div>
    </div>
  );
};

export default KPICard;
